import { ApiError } from "@/lib/apiClient";
import { pagination } from "@/types";
import { Category, CategoryState } from "@/types/models/category";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

const initialState: CategoryState = {
  items: [] as Category[],
  loading: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  form: {
    loading: false,
    success: false,
    error: null,
  },
};

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    // -------- LIST CATEGORIES --------
    fetchCategoriesRequest: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>,
    ) => {
      state.loading = true;
      state.error = null;
      state.pagination.page = action.payload.page;
    },
    fetchCategoriesSuccess: (
      state,
      action: PayloadAction<{
        list: Category[];
        pagination: pagination;
      }>,
    ) => {
      state.items = action.payload.list;
      state.pagination = action.payload.pagination;
      state.loading = false;
    },
    fetchCategoriesFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // -------- FORM SUBMISSION --------
    submitCategoryRequest: (
      state,
      action: PayloadAction<{
        data: Partial<Category>;
        collectionId?: string | null;
      }>,
    ) => {
      if (!action.payload.data) {
        return;
      }
      state.form.loading = true;
      state.form.error = null;
      state.form.success = false;
    },
    submitCategorySuccess: (
      state,
      action: PayloadAction<{ data: Category }>,
    ) => {
      state.form.loading = false;
      state.form.success = true;
      const id = action.payload.data.id;
      if (!id) {
        return;
      }
      state.items = state.items.map((item) => {
        if (item.id === id) {
          return action.payload.data;
        }
        return item;
      });
    },
    submitCategoryFailure: (state, action: PayloadAction<ApiError>) => {
      state.form.loading = false;
      state.form.error = action.payload;
      state.form.success = false;
    },
    CategoryFormReset: (state) => {
      state.form.loading = false;
      state.form.error = null;
      state.form.success = false;
    },

    // -------- DELETE CATEGORY --------
    deleteCategoryRequest: (state, action: PayloadAction<{ id: string }>) => {
      if (!action.payload.id) {
        return;
      }
      state.loading = true;
      state.error = null;
    },
    deleteCategorySuccess: (state, action: PayloadAction<{ id: string }>) => {
      state.items = state.items.filter(
        (category) => category.id !== action.payload.id,
      );
      state.loading = false;
    },
    deleteCategoryFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchCategoriesRequest,
  fetchCategoriesSuccess,
  fetchCategoriesFailure,
  submitCategoryRequest,
  submitCategorySuccess,
  submitCategoryFailure,
  deleteCategoryRequest,
  deleteCategorySuccess,
  deleteCategoryFailure,
  CategoryFormReset,
} = categorySlice.actions;

export default categorySlice.reducer;
